import Splide from "@splidejs/splide";
import ProductPrice from "./collection/components/ProductPrice";
import { h, render } from "preact";
import { isAuthorized } from "@helpers/utils";

interface Variant {
  bundle_item: BundleItem;
  millilitres: string;
  id: string;
  title: string;
  price: string;
  compare_at_price: string;
  available: boolean;
  canAmount: string;
  real_compare_at_price: string;
  price_per_can: string;
  compare_price_per_can: string;
  pfand: string;
  // Add any other properties that are expected by the ProductPrice component
}

interface BundleItem {
  title: string;
  description: string;
  content: string;
  bundleVariants: {
    id: string;
    title: string;
    price: string;
    compare_at_price: string;
    available: boolean;
    canAmount: string;
    millilitres: string;
  }[];
}

interface Product {
  real_compare_at_price: string;
  efpMember: boolean;
  fromComparePricePerCan: string;
  accountOnly: boolean;
  accessRestriction: string;
  url: string;
  canAmount: string;
  fromCanAmount: string;
  fromPricePerCan: string;
  size: number;
  style: string;
  stockistButtonLink: string;
  stockistButtonText: string;
  abv: string;
  customBadge: string;
  saleBadge: string;
  subtitle: string;
  title: string;
  handle: string;
  compare_at_price: string;
  created_at: string;
  published_at: string;
  id: string;
  images: Record<string, string>[];
  price: string;
  hidePricePdp: string;
  hidePricePlp: string;
  tags: string[];
  vendor: string;
  variants: Variant[];
  available?: boolean;
  body_html?: string;
  product_type?: string;
  updated_at?: string;
  published_scope?: string;
  template_suffix?: null | string;
  price_min?: number;
  price_max?: number;
  compare_at_price_min?: null | number;
  compare_at_price_max?: null | number;
  percent_sale_min?: number;
  synced_at_v2?: number;
  collections: [];
  options_with_values?: [];
  skus?: string[];
  barcodes?: string[];
  metafields?: string[];
  weight_min?: number;
  weight_max?: number;
  merged_search_engram?: string;
  merged_rsearch?: string;
  review_ratings?: number;
  landscapeImg: string; // Add the missing property
}

class RecentlyViewedCarousel extends HTMLElement {
  private products: Product[] = [];

  constructor() {
    super();
  }

  connectedCallback(): void {
    this.loadRecentlyViewedProducts();
    this.setupEventListeners();
    this.renderCarousel();
  }

  private setupEventListeners(): void {
    document.addEventListener(
      "recentlyViewedUpdated",
      this.renderCarousel.bind(this),
    );
  }

  private loadRecentlyViewedProducts(): void {
    const storedProducts = localStorage.getItem("recentlyViewed");

    if (storedProducts) {
      this.products = JSON.parse(storedProducts);
    }

    const themeVarsProduct = (window.ThemeVars as { product?: Product })
      ?.product;

    if (themeVarsProduct) {
      this.addProduct(themeVarsProduct);
    }
  }

  private addProduct(product: Product): void {
    if (this.products.some((p) => p.id === product.id)) {
      return;
    }
    this.products.unshift(product);
    this.updateProducts(this.products);
  }

  private updateProducts(products: Product[]): void {
    this.products = products;
    localStorage.setItem("recentlyViewed", JSON.stringify(this.products));
    document.dispatchEvent(new CustomEvent("recentlyViewedUpdated"));
  }

  private renderCarousel(): void {
    console.log("this.products: ", this.products);
    if (this.products.length > 3) {
      this.innerHTML = `
        <div class="recently-viewed-carousel__wrapper">
          <div class="splide">
            <div class="splide__track">
              <div class="splide__list">
                ${this.products
                  .map(
                    (product) => `
                  <div class="splide__slide">
                    ${this.createProductCard(product).outerHTML}
                  </div>
                `,
                  )
                  .join("")}
              </div>
            </div>
            ${this.renderCarouselControls()}
          </div>
        </div>
      `;
      this.initializeCarousel();
    } else {
      this.closest(".product-carousel")
        ?.querySelector(".product-carousel__heading")
        ?.remove();
    }
  }

  private createProductCard(product: Product): HTMLElement {
    const card = document.createElement("div");
    card.className = "product-card";
    if (!isAuthorized(product, window.themeVars.customer)) {
      card.classList.add("product-card--locked");
    }

    // Single variant out of stock logic
    const isSingleVariantOutOfStock =
      !product.available &&
      product.tags?.includes("single-variant-out-of-stock");
    const hasBisEnabled =
      isSingleVariantOutOfStock && product.tags?.includes("back-in-stock");

    if (isSingleVariantOutOfStock) {
      card.classList.add("product-card--single-variant-out-of-stock");
    }

    // Create a container for the price component
    const priceContainer = document.createElement("div");
    priceContainer.className = "product-card__price";

    let showOptions = true;
    let detailsBtnText = window.themeVars.strings.product.btnDetails;
    let detailsBtnClass =
      "product-card__details-cta btn btn-transparent btn-small";

    if (product.tags.includes("personalised")) {
      showOptions = false;
      detailsBtnText = window.themeVars.strings.product.btnPersonalise;
      detailsBtnClass =
        "product-card__details-cta btn btn-black btn-small btn-personalise";
    }

    // remove description from product object
    const liCount = [
      product.abv && 1,
      product.style && 1,
      product.size && String(product.size) !== "0" && 1,
    ].filter(Boolean).length;

    let showEfpBadge = true;
    if (product.variants.every((variant) => variant.real_compare_at_price)) {
      showEfpBadge = false;
    }

    product.variants.forEach((variant) => {
      // Check if bundle_item exists and has a description
      if (variant.bundle_item) {
        // Set the description to an empty string
        variant.bundle_item.description = "";
        variant.bundle_item.content = "";
      }
    });

    // Render the Price component into the priceContainer
    render(h(ProductPrice, { product }), priceContainer);
    const buttonText =
      product.preOrder === "true"
        ? window.themeVars.strings.product.pre_order
        : window.themeVars.strings.product.btnOptions;
    card.innerHTML = `
      <div class="product-card__content">
        <div class="product-card__images${isSingleVariantOutOfStock ? " product-card__images--out-of-stock" : ""}">
          <div class="product-card__badge-wrapper">
            ${isSingleVariantOutOfStock ? `<span class="product-card__badge badge badge-yellow">${window.themeVars.strings.product.soldOut}</span>` : ""}
            ${product.customBadge !== "no input" && product.customBadge !== undefined && !isSingleVariantOutOfStock ? `<span class="product-card__badge badge badge-orange">${product.customBadge}</span>` : ""}
            ${product.saleBadge && !isSingleVariantOutOfStock ? `<span class="product-card__badge badge badge-red">${product.saleBadge}</span>` : ""}
          </div>
          <div class="responsive-image product-card__image">
            <img width="315" height="315" src="${product.images[0]?.src}" alt="${product.title}" />
          </div>
          ${
            product.images.length > 1
              ? `
            <div class="responsive-image product-card__image product-card__image--rollover">
              <img width="315" height="315" src="${product.images[1]?.src}" alt="${product.title} rollover" />
            </div>
          `
              : ""
          }
          ${
            !isAuthorized(product, window.themeVars.customer)
              ? `
            <div class="product-lock" aria-hidden="true">
              <div class="product-lock__wrapper">
                <div class="product-lock__content">
                  <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: window.themeVars.icons.lock }}></span>
                  <div class="product-lock__text">
                    <p class="product-lock__title">${window.themeVars.strings.product.lockedTitle}</p>
                    <p class="product-lock__desc">
                      <a href="${window.themeVars.strings.product.logInLink}">${window.themeVars.strings.product.logIn}</a>&nbsp;
                      ${window.themeVars.strings.product.lockedText}&nbsp;
                      <a href="${window.themeVars.strings.product.signUpLink}">${window.themeVars.strings.product.signUp}</a>
                    </p>
                  </div>
                </div>
                <div class="product-lock__buttons">
                  <a href="${window.themeVars.strings.product.logInLink}" class="product__log-in btn btn-white btn-white--outline">${window.themeVars.strings.product.logIn}</a>
                  <a href="${window.themeVars.strings.product.signUpLink}" class="product__sign-up btn btn-white btn-white--outline">${window.themeVars.strings.product.signUp}</a>
                </div>
              </div>
            </div>
          `
              : ""
          }
        </div>
        ${
          product.abv ||
          product.style ||
          (product.size && String(product.size) !== "0")
            ? `
          <div class="product-card__info outside">
            <ul class="
              product-card__info-wrapper
              product-card__info-wrapper--${liCount}-items
            ">
              ${
                product.abv
                  ? `
                <li class="product-card__info-item">
                  <span class="product-card__info-label">${window.themeVars.strings.product.details.abv}</span>
                  <span class="product-card__info-value">${product.abv}%</span>
                </li>
              `
                  : ""
              }
              ${
                product.style
                  ? `
                <li class="product-card__info-item">
                  <span class="product-card__info-label">${window.themeVars.strings.product.details.style}</span>
                  <span class="product-card__info-value">${product.style}</span>
                </li>
              `
                  : ""
              }
              ${
                product.size && String(product.size) !== "0"
                  ? `
                <li class="product-card__info-item">
                  <span class="product-card__info-label">${window.themeVars.strings.product.details.size}</span>
                  <span class="product-card__info-value">${product.size}</span>
                </li>
              `
                  : ""
              }
            </ul>
          </div>
        `
            : ""
        }
        <div class="product-card__details">
          <div class="product-card__wrapper">
            <h3 class="product-card__title heading-4">${product.title}</h3>
            ${product.subtitle ? `<p class="product-card__subtitle text-md-body">${product.subtitle}</p>` : ""}
            ${
              product.abv ||
              product.style ||
              (product.size &&
                String(product.size) !== "0" &&
                String(product.size) !== "no input")
                ? `
              <div class="product-card__info inside">
                <ul class="
                  product-card__info-wrapper
                  product-card__info-wrapper--${liCount}-items
                ">
                  ${
                    product.abv
                      ? `
                    <li class="product-card__info-item">
                      <span class="product-card__info-label">${window.themeVars.strings.product.details.abv}</span>
                      <span class="product-card__info-value">${product.abv}%</span>
                    </li>
                  `
                      : ""
                  }
                  ${
                    product.style
                      ? `
                    <li class="product-card__info-item">
                      <span class="product-card__info-label">${window.themeVars.strings.product.details.style}</span>
                      <span class="product-card__info-value">${product.style}</span>
                    </li>
                  `
                      : ""
                  }
                  ${
                    product.size && String(product.size) !== "0"
                      ? `
                    <li class="product-card__info-item">
                      <span class="product-card__info-label">${window.themeVars.strings.product.details.size}</span>
                      <span class="product-card__info-value">${product.size}</span>
                    </li>
                  `
                      : ""
                  }
                </ul>
              </div>
            `
                : ""
            }
            ${
              window.themeVars.customer.staff && showEfpBadge
                ? `
              <p class="product-card__staff-member-badge staff-member-badge">
                <span>${window.themeVars.icons.checkmark}</span>
                ${window.themeVars.strings.product.staffDiscount}
              </p>
              `
                : window.themeVars.customer.efpMember && showEfpBadge
                  ? `
              <p class="product-card__efp-member-badge efp-member-badge">
                <span>${window.themeVars.icons.checkmark}</span>
                ${window.themeVars.strings.product.efpDiscount}
              </p>
              `
                  : ""
            }

            ${
              product.hidePricePlp !== "true"
                ? `<p class="product-card__price-container"></p>`
                : ""
            }
            

          </div>
        </div>
         <div class="product-card__buttons">
            ${
              product.stockistButtonLink && product.stockistButtonText
                ? `<a
                href="${product.stockistButtonLink}"
                class="product-card__options-cta btn btn-black btn-small"
              >
                ${product.stockistButtonText}
              </a>`
                : `${
                    showOptions && !isSingleVariantOutOfStock
                      ? `
               <uwp-quickshop-button data-product='${JSON.stringify(product as any)}'>
                <button
                  aria-label="${window.themeVars.strings.product.quickshopLabel}${product.title}"
                  data-quickshop="${product.handle}"
                  class="product-card__options-cta btn btn-black btn-small"
                >
                  ${buttonText}
                </button>
              </uwp-quickshop-button>`
                      : isSingleVariantOutOfStock && hasBisEnabled
                        ? `<button type="button" class="product-card__options-cta btn btn-black btn-small" data-bis-trigger data-product="${product.id}" data-variant="${product.variants[0]?.id}"><span>${window.themeVars.strings.product.notifyMe}</span></button>`
                        : ""
                  }         `
            }
            <a href="${window.Shopify.routes.root}products/${product.handle}" class="${detailsBtnClass}" aria-label="${window.themeVars.strings.product.linkLabel}${product.title}">${detailsBtnText}</a>
          </div>

          ${hasBisEnabled ? `<uwp-product-notify-me data-product-id="${product.id}" data-variant-id="" product-data='${JSON.stringify(product)}' is-visible="false"></uwp-product-notify-me>` : ""}
      </div>
    `;

    card
      .querySelector(".product-card__price-container")
      ?.appendChild(priceContainer);

    // Add event listener for notify me functionality
    if (hasBisEnabled) {
      setTimeout(() => {
        const bisButton = card.querySelector(
          `[data-bis-trigger][data-product="${product.id}"]`,
        ) as HTMLElement;

        const bisPopup = card.querySelector(
          `uwp-product-notify-me[data-product-id="${product.id}"]`,
        ) as HTMLElement;

        if (bisButton && bisPopup) {
          const handleBisClick = () => {
            bisPopup.setAttribute("is-visible", "true");
          };

          bisButton.addEventListener("click", handleBisClick);
        }
      }, 100); // Small delay to ensure DOM is ready
    }

    return card;
  }

  private renderCarouselControls(): string {
    return `
    <div class="carousel-controls">
      <div class="carousel-controls__scrollbar-track">
        <div class="carousel-controls__scrollbar-progress" js-carousel-controls="scrollbarProgress"></div>
      </div>
      <div class="carousel-controls__arrow-wrapper splide__arrows splide__arrows--ltr">
        <button class="splide__arrow carousel-controls__arrow splide__arrow--prev" type="button" aria-label="Previous">
          ${window.themeVars.icons.chevronLeft}
        </button>
        <button class="splide__arrow carousel-controls__arrow splide__arrow--next" type="button" aria-label="Next">
          ${window.themeVars.icons.chevronRight}
        </button>
      </div>
    </div>
    `;
  }

  private initializeCarousel(): void {
    const splideElement = this.querySelector(".splide") as HTMLElement | null;

    if (splideElement) {
      const splide = new Splide(splideElement, {
        type: "loop",
        perPage: 3,
        perMove: 1,
        pagination: true,
        padding: {
          right: "10%",
        },
        gap: 16,
        arrows: true,
        breakpoints: {
          640: {
            perPage: 1,
            padding: {
              right: "20%",
            },
          },
        },
      }).mount();

      const scrollBar = this.querySelector(
        "[js-carousel-controls='scrollbarProgress']",
      ) as HTMLElement;

      if (scrollBar) {
        splide.on("mounted move", () => {
          const end = splide.Components.Controller.getEnd() + 1;
          const rate = Math.min((splide.index + 1) / end, 1);
          scrollBar.style.width = String(100 * rate) + "%";
        });
      }
    } else {
      console.error("Splide element not found");
    }
  }
}

customElements.define("uwp-recently-viewed-carousel", RecentlyViewedCarousel);
